// Tournamentlist.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./tournament.css";

function Tournamentlist() {
  const navigate = useNavigate();

  const [tournaments, setTournaments] = useState([]);
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchTournaments = async () => {
      const token = localStorage.getItem("token");
      console.log("Token:", token);
      
      try {
        const headers = {};
        
        // Only add Authorization header if token exists
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await fetch(
          "https://api.asterickx.com/api/Tournaments",
          { headers }
        );
        
        // If unauthorized and we had a token, clear it but continue to show tournaments
        if (response.status === 401 && token) {
          console.log("Token expired, clearing from storage");
          localStorage.removeItem("token");
        }
        
        if (!response.ok && response.status !== 401) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        // If 401, try to fetch without token (public access)
        let data;
        if (response.status === 401) {
          const publicResponse = await fetch(
            "https://api.asterickx.com/api/Tournaments"
          );
          if (publicResponse.ok) {
            data = await publicResponse.json();
          } else {
            throw new Error("Unable to fetch tournaments");
          }
        } else {
          data = await response.json();
        }
        
        console.log("Tournament data:", data);

        // Sort tournaments by start date (no filtering)
        const sortedTournaments = data.sort(
          (a, b) => new Date(a.startDate) - new Date(b.startDate)
        );

        setTournaments(sortedTournaments);
        setFilteredTournaments(sortedTournaments);
      } catch (err) {
        console.error("Error fetching tournaments:", err);
        setError("Failed to load tournaments. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, [navigate]);

  // Handle search functionality
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredTournaments(tournaments);
    } else {
      const filtered = tournaments.filter((tournament) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          tournament.name?.toLowerCase().includes(searchLower) ||
          tournament.venue?.toLowerCase().includes(searchLower) ||
          tournament.status?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredTournaments(filtered);
    }
  }, [searchQuery, tournaments]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleView = async (tournament) => {
    console.log("Viewing tournament:", tournament);
    
    try {
      const token = localStorage.getItem("token");
      const headers = {};
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      // Fetch full tournament details
      const response = await fetch(
        `https://api.asterickx.com/api/Tournaments/${tournament.id}/full`,
        { headers }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch tournament details");
      }
      
      const fullTournamentData = await response.json();
      console.log("Full tournament data:", fullTournamentData);
      
      // Navigate to tournament view page with full data
      navigate("/tournament-setup", { 
        state: { 
          tournamentData: fullTournamentData,
          isView: true
        } 
      });
    } catch (err) {
      console.error("Error fetching tournament details:", err);
      alert("Failed to load tournament details. Please try again.");
    }
  };

  const handleEdit = async (tournament) => {
    console.log("Editing tournament:", tournament);
    
    try {
      const token = localStorage.getItem("token");
      const headers = {};
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      // Fetch full tournament details including teams, sponsors, sessions
      const response = await fetch(
        `https://api.asterickx.com/api/Tournaments/${tournament.id}/full`,
        { headers }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch tournament details");
      }
      
      const fullTournamentData = await response.json();
      console.log("Full tournament data for edit:", fullTournamentData);
      
      // Navigate to tournament-setup page with full tournament data
      navigate("/tournament-setup", { 
        state: { 
          tournamentData: fullTournamentData,
          isEdit: true
        } 
      });
    } catch (err) {
      console.error("Error fetching tournament details:", err);
      alert("Failed to load tournament details. Please try again.");
    }
  };

  const handleCreateTournament = () => {
    navigate("/tournament-setup");
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Function to determine status dot color
  const getStatusDotClass = (status) => {
    if (!status) return 'status-dot-inactive';
    const statusLower = status.toLowerCase();
    return statusLower === 'active' ? 'status-dot-active' : 'status-dot-inactive';
  };

  return (
    <div className="container mt-5" style={{ marginBottom: "10rem" }}>
      <div className="header-section">
        <h2 className="page-title">Tournament List</h2>
        <div className="header-actions">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search tournaments..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <svg 
              className="search-icon" 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>
          <button 
            className="btn-create-tournament"
            onClick={handleCreateTournament}
          >
            <span className="btn-icon">+</span>
            Add New Tournament
          </button>
        </div>
      </div>
      
      {error && <p className="text-danger text-center">{error}</p>}
      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead className="table-dark">
            <tr>
              <th>Tournament Name</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Venue</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredTournaments.length > 0 ? (
              filteredTournaments.map((tournament) => (
                <tr key={tournament.id}>
                  <td>{tournament.name}</td>
                  <td>{formatDate(tournament.startDate)}</td>
                  <td>{formatDate(tournament.endDate)}</td>
                  <td>{tournament.venue}</td>
                  <td>
                    <div className="status-dot-container">
                      <span className={`status-dot ${getStatusDotClass(tournament.status)}`}></span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-action btn-view"
                        onClick={() => handleView(tournament)}
                        disabled={loading}
                        title="View"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                      <button
                        className="btn-action btn-edit"
                        onClick={() => handleEdit(tournament)}
                        disabled={loading}
                        title="Edit"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                          <path d="m15 5 4 4"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  {searchQuery ? "No tournaments found matching your search" : "No tournaments available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Tournamentlist;